import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import type { ChatListStyles } from '../theme/chatListStylesFromPalette';

/** Show warning when next server purge is within this window (matches «остался день»). */
const WARNING_WINDOW_MS = 48 * 60 * 60 * 1000;

function parsePurgeEndMs(raw: string): number {
  const s = raw.trim();
  let ms = Date.parse(s);
  if (Number.isFinite(ms)) return ms;
  const trimmed = s.replace(/(\.\d{3})\d+(?=Z|[+-])/i, '$1');
  if (trimmed !== s) {
    ms = Date.parse(trimmed);
    if (Number.isFinite(ms)) return ms;
  }
  return Number.NaN;
}

function formatHms(remainingMs: number): string {
  const ms = Math.max(0, remainingMs);
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

type Props = {
  purgeAtIso: string;
  policyTitle: string;
  policyBody: string;
  confirmLabel: string;
  a11yLabel: string;
  styles: Pick<ChatListStyles, 'retentionTimerPressable' | 'retentionTimerText'>;
};

export const DirectRetentionCountdown = React.memo(function DirectRetentionCountdown({
  purgeAtIso,
  policyTitle,
  policyBody,
  confirmLabel,
  a11yLabel,
  styles,
}: Props) {
  const [, setTick] = useState(0);
  const [policyOpen, setPolicyOpen] = useState(false);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const policyModalStyles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          width: '100%',
          maxWidth: 400,
          borderRadius: 14,
          backgroundColor: '#ffffff',
          paddingHorizontal: 18,
          paddingTop: 16,
          paddingBottom: 14,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: 'rgba(148, 163, 184, 0.45)',
        },
        modalTitle: {
          fontSize: 17,
          fontWeight: '800',
          color: '#0f172a',
          marginBottom: 10,
        },
        modalBody: {
          fontSize: 14,
          lineHeight: 21,
          color: '#334155',
        },
        modalBtn: {
          marginTop: 14,
          alignSelf: 'flex-end',
          paddingVertical: 10,
          paddingHorizontal: 16,
          borderRadius: 10,
          backgroundColor: '#e2e8f0',
        },
        modalBtnText: {
          fontSize: 15,
          fontWeight: '700',
          color: '#0f172a',
        },
      }),
    [],
  );

  const end = parsePurgeEndMs(purgeAtIso);
  const remaining = Number.isFinite(end) ? end - Date.now() : Number.NaN;
  if (!Number.isFinite(remaining) || remaining <= 0 || remaining > WARNING_WINDOW_MS) {
    return null;
  }

  return (
    <>
      <Pressable
        style={styles.retentionTimerPressable}
        onPress={() => {
          setPolicyOpen(true);
        }}
        accessibilityRole="button"
        accessibilityLabel={a11yLabel}>
        <Text style={styles.retentionTimerText}>⏱ {formatHms(remaining)}</Text>
      </Pressable>
      <Modal
        visible={policyOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setPolicyOpen(false)}>
        <View style={{ flex: 1 }}>
          <Pressable
            style={[StyleSheet.absoluteFillObject, { backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: 0 }]}
            onPress={() => setPolicyOpen(false)}
            accessibilityRole="button"
          />
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              padding: 24,
              zIndex: 1,
              pointerEvents: 'box-none',
            }}>
            <View style={policyModalStyles.card}>
              <Text style={policyModalStyles.modalTitle}>{policyTitle}</Text>
              <ScrollView style={{ maxHeight: 320 }} keyboardShouldPersistTaps="handled">
                <Text style={policyModalStyles.modalBody}>{policyBody}</Text>
              </ScrollView>
              <Pressable
                style={policyModalStyles.modalBtn}
                onPress={() => setPolicyOpen(false)}
                accessibilityRole="button">
                <Text style={policyModalStyles.modalBtnText}>{confirmLabel}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
});
